import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Divider,
  Form,
  Col,
  Row,
  Input,
  Select,
  DatePicker,
  Button,
  Tooltip,
  Checkbox,
  Modal,
} from "antd";
import ButtonGroup from "antd/lib/button/button-group";
import { jwtDecode } from "jwt-decode";
import AuthService from "../../services/AuthService";
import {
  MENU_ACCOUNTS,
  MENU_ACCOUNTS_WRITE,
  MENU_BILLING_ROLLBACK_WRITE,
  MENU_ACCOUNTS_INDIVIDUAL,
  MENU_ACCOUNTS_INDIVIDUAL_WRITE,
  MENU_ACCOUNTS_ORGANIZATION,
  MENU_ACCOUNTS_ORGANIZATION_WRITE,
} from "../../lib/const/permissions";
import {
  displayFormat,
  toHttpFormatStartOfDay,
  toHttpFormatEndOfDay,
} from "../../lib/utils/formatDate";
import { deleteCharges } from "../../store/slices/charges";
import { hasAccess } from "../RolesModule/hasAccess";
import { deleteInvoices } from "../../store/slices/invoices";
import { deleteBills } from "../../store/slices/bills";
import { AgreementsSearchModal } from "../AgreementsSearch";
import { agreementsSearchActions } from "../../store/slices/agreementsSearch/agreementsSearch";
import { IClientType } from "../../lib/types";
import { ReactComponent as LoupeIcon } from "../../img/loupe-icon.svg";
import { limitText } from "../../lib/utils/limitText";
import { AccessToken } from "../../lib/types/AccessToken";
import "./BillingRollback.less";

interface IBillingRollback {
  [key: string]: any;
}

const BillingRollback: React.FC<IBillingRollback> = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const token = AuthService.getCurrentUser();
  const displayName = token ? jwtDecode<AccessToken>(token).display_name : undefined;

  const [visibleDeleteOnlyAvailable, setVisibleDeleteOnlyAvailable] = useState(false);

  const [searchClientType, setSearchClientType] = useState<IClientType>("ORGANIZATION");

  const hasAccessToOrganization: boolean = useMemo(
    () =>
      hasAccess(
        MENU_ACCOUNTS_ORGANIZATION,
        MENU_ACCOUNTS_ORGANIZATION_WRITE,
        MENU_ACCOUNTS,
        MENU_ACCOUNTS_WRITE
      ),
    [hasAccess]
  );

  const hasAccessToIndividual = useMemo(
    () => hasAccess(MENU_ACCOUNTS_INDIVIDUAL, MENU_ACCOUNTS_INDIVIDUAL_WRITE),
    [hasAccess]
  );

  // Кнопка изменения задизейблена, если доступен только один вид клиента
  const allowedClientChange = useMemo(() => {
    if (hasAccessToOrganization && hasAccessToIndividual) {
      return true;
    }
    return false;
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  // Дефолтный тип клиента в поиске ЮЛ/ФЛ
  useEffect(() => {
    if (hasAccessToOrganization) {
      setSearchClientType("ORGANIZATION");
    } else if (hasAccessToIndividual) {
      setSearchClientType("INDIVIDUAL");
    }
  }, [hasAccessToOrganization, hasAccessToIndividual]);

  const toggleSearchClientType = () => {
    if (allowedClientChange) {
      setSearchClientType((prev) => (prev === "INDIVIDUAL" ? "ORGANIZATION" : "INDIVIDUAL"));
      dispatch(agreementsSearchActions.cleanAccounts());
    }
  };

  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const onCloseSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  const onSubmitSearchModal = (
    v,
    agreement?: {
      agreementId: number;
      agreementNumber: string;
      fullName: string;
      accountId: number;
    }[]
  ) => {
    onCloseSearchModal();
    if (agreement) {
      // console.log(agreement[0]);
      form.setFieldsValue({ client: agreement[0] });
    }
    // console.log(agreements);
  };

  // Очистка списка accountSelect при входе и выходе
  useEffect(() => {
    dispatch(agreementsSearchActions.cleanAccounts());
    return () => {
      dispatch(agreementsSearchActions.cleanAccounts());
    };
  }, []);

  const [createTaskModalVisible, setCreateTaskModalVisible] = useState(false);
  const showCreateTaskModal = () => {
    form.validateFields().then(() => {
      setCreateTaskModalVisible(true);
    });
  };
  const closeCreateTaskModal = () => setCreateTaskModalVisible(false);

  const handleSelectTaskId = (id) => {
    if (id === 3) {
      setVisibleDeleteOnlyAvailable(true);
    } else {
      setVisibleDeleteOnlyAvailable(false);
      form.setFieldsValue({ deleteOnlyAvailable: false });
    }
  };

  const handleChangeDeleteOnlyAvailable = (event) => {
    form.setFieldsValue({ deleteOnlyAvailable: event.target.checked });
  };

  const taskList = [
    { id: 1, name: "Удаление счетов на постоплату" },
    { id: 2, name: "Удаление инвойсов" },
    { id: 3, name: "Удаление начислений" },
  ];

  const handleDeleteBills = ({ client, range }) => {
    closeCreateTaskModal();
    dispatch(
      // @ts-ignore
      deleteBills({
        agreementId: client.agreementId,
        periodFrom: toHttpFormatStartOfDay(range[0]),
        periodTo: toHttpFormatEndOfDay(range[1]),
        useCoveringVariant: 3,
      })
    );
  };

  const handleDeleteCharges = ({ client, range, deleteOnlyAvailable = false }) => {
    closeCreateTaskModal();
    dispatch(
      // @ts-ignore
      deleteCharges({
        agreementId: client.agreementId,
        periodFrom: toHttpFormatStartOfDay(range[0]),
        periodTo: toHttpFormatEndOfDay(range[1]),
        deleteMode: deleteOnlyAvailable,
      })
    );
  };

  const handleDeleteInvoices = ({ client, range }) => {
    closeCreateTaskModal();
    dispatch(
      // @ts-ignore
      deleteInvoices({
        agreementId: client.agreementId,
        effectiveDateFrom: toHttpFormatStartOfDay(range[0]),
        effectiveDateTo: toHttpFormatEndOfDay(range[1]),
      })
    );
  };

  const handleSubmit = (values) => {
    switch (values.taskId) {
      case 1:
        return handleDeleteBills(values);
      case 2:
        return handleDeleteInvoices(values);
      case 3:
        return handleDeleteCharges(values);
      default:
        return null;
    }
  };

  return (
    <div>
      <Divider style={{ marginTop: "3px", marginBottom: "10px" }} />
      <h3>Параметры запуска</h3>
      <Form style={{ marginTop: "32px" }} onFinish={handleSubmit} form={form}>
        <Row gutter={30}>
          <Col span={10}>
            <Form.Item
              wrapperCol={{ span: 24 }}
              shouldUpdate
              className="form-item-required__hidden-error__mb0 billing-rollback__agreement"
            >
              {({ getFieldValue }) => {
                const client = getFieldValue("client");
                const displayValue = client
                  ? `${client?.agreementNumber} / ${client.fullName}`
                  : "";
                return (
                  <Form.Item
                    labelCol={{ span: 7 }}
                    wrapperCol={{ span: 17 }}
                    label={
                      <div>
                        Клиент
                        <Tooltip title="Открыть расширенный поиск">
                          <LoupeIcon
                            onClick={() => {
                              setIsSearchModalOpen(true);
                            }}
                          />
                        </Tooltip>
                      </div>
                    }
                    name="client"
                    className="form-item-required__hidden-error"
                  >
                    <Tooltip title={displayValue} placement="topLeft">
                      <div>
                        <Input
                          style={{
                            width: "300px",
                            textOverflow: "ellipsis",
                            paddingRight: "25px",
                          }}
                          value={limitText(displayValue, 40)}
                          onClick={() => setIsSearchModalOpen(true)}
                          disabled
                          placeholder="Договор не выбран"
                        />
                      </div>
                    </Tooltip>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item
              label="Задание"
              name="taskId"
              rules={[{ required: true }]}
              className="form-item-required__hidden-error"
            >
              <Select style={{ width: "300px" }} onChange={handleSelectTaskId}>
                {taskList.map(({ id, name }) => (
                  <Select.Option key={id} value={id}>
                    <div>{name}</div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={30}>
          <Col span={10}>
            <Form.Item
              wrapperCol={{ span: 24 }}
              dependencies={["taskId"]}
              className="form-item-required__hidden-error"
            >
              {({ getFieldValue }) => {
                const taskId = getFieldValue("taskId");
                const label = taskId === 2 ? "Дата счета-фактуры" : "Период";
                return (
                  <Form.Item
                    label={label}
                    name="range"
                    rules={[{ required: true }]}
                    className="form-item-required__hidden-error"
                    labelCol={{ span: 7 }}
                    wrapperCol={{ span: 17 }}
                  >
                    <DatePicker.RangePicker style={{ width: "300px" }} format={displayFormat} />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col span={16}>
            {visibleDeleteOnlyAvailable && (
              <Form.Item
                name="deleteOnlyAvailable"
                className="form-item-required__hidden-error"
                style={{ marginLeft: "13px" }}
              >
                <Checkbox id="deleteOnlyAvailable" onChange={handleChangeDeleteOnlyAvailable} />
                <label
                  style={{ marginLeft: "13px", cursor: "pointer" }}
                  htmlFor="deleteOnlyAvailable"
                >
                  Удалять только доступные
                </label>
              </Form.Item>
            )}
          </Col>
        </Row>
        <Divider style={{ marginTop: "100px" }} />
        <Row>
          <ButtonGroup style={{ marginLeft: "auto", marginRight: "auto" }}>
            {hasAccess(MENU_BILLING_ROLLBACK_WRITE) && (
              <Tooltip title={form.getFieldValue("client") ? "" : "Необходимо выбрать договор"}>
                <Button
                  type="primary"
                  htmlType="button"
                  onClick={showCreateTaskModal}
                  disabled={!form.getFieldValue("client")}
                >
                  Создать задание
                </Button>
              </Tooltip>
            )}
          </ButtonGroup>
        </Row>
      </Form>
      <AgreementsSearchModal
        clientType={searchClientType}
        toggleClientType={toggleSearchClientType}
        visible={isSearchModalOpen}
        onClose={onCloseSearchModal}
        onSubmit={onSubmitSearchModal}
        okText="Выбрать договор"
      />
      <Modal
        onCancel={closeCreateTaskModal}
        visible={createTaskModalVisible}
        width={500}
        style={{ top: "25%" }}
        footer={[
          <Button key="1" onClick={closeCreateTaskModal} style={{ width: "108px" }}>
            Отмена
          </Button>,
          <Button key="2" onClick={form.submit} type="primary" style={{ width: "108px" }}>
            Далее
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <div style={{ fontSize: "14px", color: "#5D6C7A", marginBottom: "25px" }}>
            Будет сформировано задание на выполнение операции:
          </div>
          <div style={{ fontSize: "16px", color: "#112244", marginBottom: "25px" }}>
            {taskList.find(({ id }) => id === form.getFieldValue("taskId"))?.name}
          </div>
          <div style={{ fontSize: "13px", marginBottom: "25px" }}>
            <span style={{ color: "#5D6C7A" }}>Инициатор: </span>
            <span style={{ color: "000" }}>{displayName}</span>
          </div>
          <div style={{ fontSize: "16px", color: "#112244", marginBottom: "5px" }}>
            Для продолжения нажмите «Далее»
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BillingRollback;
